import React from "react";
import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import type { Transition } from "@veyso/core";
import { easingFor } from "./easing.js";

export interface TransitionPhase {
  transition: Transition;
  frames: number;
}

export interface TransitionStyles {
  style: React.CSSProperties;
  /** Rendered above the section, e.g. the black dip of a `fade`. */
  veil?: React.CSSProperties;
}

/**
 * Exported because the editor's transition picker draws its preview tiles from
 * these. A tile that showed a hand-drawn impression of a wipe would be a second
 * opinion about what a wipe is; reading the same function means the picture on
 * the tile is the effect that will be rendered.
 *
 * Sections overlap by the transition's duration — BODY is already on screen
 * while HEAD is still finishing — so a transition is expressed as two halves:
 * how the outgoing section leaves and how the incoming one arrives. Both read
 * from the same `Transition` object, so the pair always agrees.
 */
export function enterStyles(transition: Transition, p: number): TransitionStyles {
  switch (transition.type) {
    case "none":
      return { style: { opacity: p < 1 ? 0 : 1 } };
    case "fade":
      return {
        style: { opacity: interpolate(p, [0.4, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }) },
      };
    case "crossfade":
      return { style: { opacity: p } };
    case "slide-left":
      return { style: { transform: `translateX(${(1 - p) * 100}%)` } };
    case "slide-right":
      return { style: { transform: `translateX(${-(1 - p) * 100}%)` } };
    case "slide-up":
      return { style: { transform: `translateY(${(1 - p) * 100}%)` } };
    case "slide-down":
      return { style: { transform: `translateY(${-(1 - p) * 100}%)` } };
    case "zoom":
      return { style: { opacity: p, transform: `scale(${interpolate(p, [0, 1], [1.18, 1])})` } };
    case "blur":
      return { style: { opacity: p, filter: `blur(${(1 - p) * 24}px)` } };
    case "wipe":
      return { style: { clipPath: `inset(0 ${(1 - p) * 100}% 0 0)` } };
    case "circle-reveal":
      return { style: { clipPath: `circle(${p * 80}% at 50% 50%)` } };
    default:
      return { style: { opacity: p } };
  }
}

export function exitStyles(transition: Transition, q: number): TransitionStyles {
  switch (transition.type) {
    case "none":
      return { style: {} };
    case "fade":
      return {
        style: {},
        // Dip through black: opaque at the midpoint, clear at both ends.
        veil: { backgroundColor: "#000", opacity: interpolate(q, [0, 0.5, 1], [0, 1, 0]) },
      };
    case "crossfade":
      return { style: { opacity: 1 - q } };
    case "slide-left":
      return { style: { transform: `translateX(${-q * 40}%)`, opacity: 1 - q * 0.4 } };
    case "slide-right":
      return { style: { transform: `translateX(${q * 40}%)`, opacity: 1 - q * 0.4 } };
    case "slide-up":
      return { style: { transform: `translateY(${-q * 40}%)`, opacity: 1 - q * 0.4 } };
    case "slide-down":
      return { style: { transform: `translateY(${q * 40}%)`, opacity: 1 - q * 0.4 } };
    case "zoom":
      return { style: { transform: `scale(${interpolate(q, [0, 1], [1, 0.88])})`, opacity: 1 - q } };
    case "blur":
      return { style: { filter: `blur(${q * 24}px)`, opacity: 1 - q } };
    case "wipe":
    case "circle-reveal":
      // The incoming section is drawn on top and does the revealing; the
      // outgoing one simply holds until it is covered.
      return { style: {} };
    default:
      return { style: { opacity: 1 - q } };
  }
}

export function mergeTransitionStyles(a: React.CSSProperties, b: React.CSSProperties): React.CSSProperties {
  const transform = [a.transform, b.transform].filter(Boolean).join(" ");
  const filter = [a.filter, b.filter].filter(Boolean).join(" ");
  return {
    ...a,
    ...b,
    opacity:
      (a.opacity ?? 1) === 1 ? b.opacity : (b.opacity ?? 1) === 1 ? a.opacity : Number(a.opacity) * Number(b.opacity),
    ...(transform ? { transform } : {}),
    ...(filter ? { filter } : {}),
  };
}

export const TransitionWrapper: React.FC<{
  durationInFrames: number;
  enter?: TransitionPhase;
  exit?: TransitionPhase;
  children: React.ReactNode;
}> = ({ durationInFrames, enter, exit, children }) => {
  const frame = useCurrentFrame();

  let style: React.CSSProperties = {};
  let veil: React.CSSProperties | undefined;

  if (enter && enter.frames > 0) {
    const p = interpolate(frame, [0, enter.frames], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: easingFor(enter.transition.easing),
    });
    const result = enterStyles(enter.transition, p);
    style = mergeTransitionStyles(style, result.style);
    veil = result.veil ?? veil;
  }

  if (exit && exit.frames > 0) {
    const start = Math.max(0, durationInFrames - exit.frames);
    const q = interpolate(frame, [start, durationInFrames], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: easingFor(exit.transition.easing),
    });
    const result = exitStyles(exit.transition, q);
    style = mergeTransitionStyles(style, result.style);
    if (result.veil) veil = result.veil;
  }

  return (
    <AbsoluteFill style={style}>
      {children}
      {veil ? <AbsoluteFill style={veil} /> : null}
    </AbsoluteFill>
  );
};
